import React, { useState } from 'react';
import { StandardListLayout } from '@/shared/layout/StandardListLayout';
import { DataFilterToolbar } from '@/components/ui/DataFilterToolbar';
import { KpiGrid, type KpiItem } from '@/components/ui/KpiGrid';
import { useNotifications } from './hooks/useNotifications';
import { useAuth } from '@/core/auth/AuthContext';
import { NotificationCard } from './components/NotificationCard';
import { NotificationDetailPanel } from './components/NotificationDetailPanel';
import { TablePagination } from '@/components/ui/TablePagination';
import { NotificationItem } from './types/notifications.types';
import { Search, CheckCheck, Bell, BellRing, UserCircle, AlertTriangle, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { permissionEngine } from '@/core/permissions/permissionEngine';

export default function NotificationListPage() {
  const { profile } = useAuth();
  
  // Guard access
  permissionEngine.assert(profile?.id || '', 'notifications.view');

  const {
    notifications,
    isLoading,
    unreadCount,
    markAsRead,
    markAllAsRead
  } = useNotifications(profile?.company_id || '', profile?.id || '');

  const [filterValues, setFilterValues] = useState({ tab: 'all', type: 'all' });
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'table' | 'card' | 'compact'>('card');
  const pageSize = 10;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(paginatedData.map(n => n.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(item => item !== id));
    }
  };

  const handleNotificationClick = async (item: NotificationItem) => {
    setSelectedNotification(item);
    if (item.status_code === 'unread') {
      await markAsRead(item.id);
    }
  };

  // Pagination logic
  const filteredNotifications = notifications.filter(n => {
    if (filterValues.tab === 'unread') return n.status_code === 'unread';
    // Add logic for mentions if needed based on message or type
    return true;
  });

  const totalItems = filteredNotifications.length;
  const paginatedData = filteredNotifications.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const kpis: KpiItem[] = [
    { title: 'Tổng thông báo', value: totalItems, suffix: 'Tin', icon: Bell, color: 'text-blue-600', bg: 'bg-blue-50', trend: 'neutral', trendValue: 'Hôm nay', chartColor: 'bg-blue-500', chartData: [5, 6, 7, 8, 10, totalItems, totalItems] },
    { title: 'Chưa đọc', value: unreadCount, suffix: 'Tin', icon: BellRing, color: 'text-rose-600', bg: 'bg-rose-50', trend: 'up', trendValue: 'Mới', chartColor: 'bg-rose-500', chartData: [1, 2, 3, 2, 4, unreadCount, unreadCount] },
    { title: 'Nhắc đến tôi', value: 0, suffix: 'Tin', icon: UserCircle, color: 'text-purple-600', bg: 'bg-purple-50', trend: 'neutral', trendValue: '@mentions', chartColor: 'bg-purple-500', chartData: [0, 0, 0, 0, 0, 0, 0] },
    { title: 'Phê duyệt', value: 0, suffix: 'Tin', icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', trend: 'up', trendValue: 'Cần duyệt', chartColor: 'bg-emerald-500', chartData: [0, 0, 0, 0, 0, 0, 0] },
    { title: 'Công việc mới', value: 0, suffix: 'Tin', icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', trend: 'neutral', trendValue: 'Assigned', chartColor: 'bg-amber-500', chartData: [0, 0, 0, 0, 0, 0, 0] },
    { title: 'Cảnh báo', value: 0, suffix: 'Tin', icon: ShieldAlert, color: 'text-slate-600', bg: 'bg-slate-50', trend: 'neutral', trendValue: 'System', chartColor: 'bg-slate-500', chartData: [0, 0, 0, 0, 0, 0, 0] },
  ];

  const filterDefs = [
    {
      id: 'tab',
      label: 'Danh mục',
      options: [
        { value: 'all', label: 'Tất cả' },
        { value: 'unread', label: `Chưa đọc (${unreadCount})` },
        { value: 'mentions', label: 'Đề cập tôi' }
      ]
    },
    {
      id: 'type',
      label: 'Phân loại',
      options: [
        { value: 'all', label: 'Tất cả loại' },
        { value: 'approval', label: 'Phê duyệt' },
        { value: 'task', label: 'Công việc' },
        { value: 'alert', label: 'Cảnh báo' }
      ]
    }
  ];

  return (
    <StandardListLayout
      title="Thông báo"
      description="Cập nhật các thông tin, nhắc việc và cảnh báo từ hệ thống"
      isLoading={isLoading}
      viewMode={viewMode}
      kpiSection={<KpiGrid kpis={kpis} />}
      toolbarSection={
        <DataFilterToolbar
          search=""
          onSearchChange={() => {}}
          searchPlaceholder="Tìm kiếm thông báo..."
          filterDefs={filterDefs}
          filterValues={filterValues}
          onFilterChange={(k, v) => setFilterValues(prev => ({ ...prev, [k]: v }))}
          primaryAction={{
            label: "Đánh dấu đã đọc tất cả",
            icon: CheckCheck,
            onClick: () => markAllAsRead()
          }}
        />
      }
      tableSection={
        <div className="p-8 text-center text-slate-500">
          Chế độ bảng không khả dụng cho thông báo. Vui lòng chuyển sang chế độ thẻ.
        </div>
      }
      cardSection={
        <div className="flex flex-col h-full bg-white">
          {paginatedData.length > 0 && (
            <div className="flex items-center gap-4 p-3 border-b border-slate-100 bg-slate-50/80 sticky top-0 z-10">
              <div className="shrink-0 pl-2">
                <input
                  type="checkbox"
                  checked={selectedIds.length === paginatedData.length && paginatedData.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600 cursor-pointer"
                />
              </div>
              <div className="sm:text-sm text-xs text-slate-500 font-medium">Chọn tất cả</div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {paginatedData.length > 0 ? (
              <div>
                {paginatedData.map((item) => (
                  <NotificationCard
                    key={item.id}
                    item={item}
                    isSelected={selectedIds.includes(item.id)}
                    onSelect={(checked) => handleSelectOne(item.id, checked)}
                    onClick={handleNotificationClick}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-slate-300" />
                </div>
                <p className="text-slate-500 sm:text-sm text-xs">Không có thông báo nào.</p>
              </div>
            )}
          </div>
          {totalItems > 0 && (
            <div className="border-t border-slate-100 p-3 bg-white shrink-0">
              <TablePagination
                currentPage={currentPage}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                itemLabel="thông báo"
              />
            </div>
          )}
        </div>
      }
      sidePanel={
        selectedNotification && (
          <NotificationDetailPanel 
            item={selectedNotification} 
            onClose={() => setSelectedNotification(null)} 
          />
        )
      }
    />
  );
}
